import React from "react";
import { Link } from "react-router-dom";
import { 
  Facebook, 
  Linkedin, 
  Instagram, 
  Youtube,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Send
} from "lucide-react";
import logo from "../../assets/newlogo.png"; // Updated logo path

// X (Twitter) icon as SVG component
const XIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Footer() {
  const currentYear = new Date().getFullYear();

  // Social media links with valid URLs
  const socialLinks = [
    { icon: Facebook, color: '#1877f2', url: 'https://www.facebook.com/profile.php?id=61589254807287', name: 'Facebook' },
    { icon: XIcon, color: '#000000', url: 'https://x.com/ZapDialIndia', name: 'X (Twitter)' },
    { icon: Linkedin, color: '#0a66c2', url: 'https://www.linkedin.com/feed/', name: 'LinkedIn' },
    { icon: Instagram, color: '#e4405f', url: 'https://instagram.com/zapdial', name: 'Instagram' },
    { icon: Youtube, color: '#ff0000', url: 'https://youtube.com/@zapdial?si=NRFqkOZhC5PK91WJ', name: 'YouTube' }
  ];

  const styles = {
    footer: {
      backgroundColor: '#1e293b', // Dark gray-blue (slate)
      color: '#e2e8f0',
      padding: '60px 0 20px',
      position: 'relative',
      marginTop: '50px',
      borderTop: '1px solid #334155'
    },
    container: {
      maxWidth: '1300px',
      margin: '0 auto',
      padding: '0 32px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '40px',
      marginBottom: '40px'
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    },
    logoContainer: {
      marginBottom: '50px'
    },
    logo: {
      width: '250px',
      height: 'auto',
      objectFit: 'contain',
      // No filter - original logo colors preserved
    },
    description: {
      fontSize: '14px',
      color: '#94a3b8',
      lineHeight: '1.6',
      marginBottom: '16px'
    },
    socialLinks: {
      display: 'flex',
      gap: '12px'
    },
    socialButton: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: '#334155',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#cbd5e1',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: 'none',
      padding: 0
    },
    columnTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#f1f5f9'
    },
    linkList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    link: {
      color: '#94a3b8',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: '#94a3b8',
      fontSize: '14px',
      marginBottom: '12px'
    },
    newsletterBox: {
      backgroundColor: '#0f172a',
      padding: '20px',
      borderRadius: '12px',
      marginTop: '20px'
    },
    newsletterTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#f1f5f9'
    },
    newsletterText: {
      fontSize: '12px',
      color: '#94a3b8',
      marginBottom: '12px',
      lineHeight: '1.5'
    },
    inputGroup: {
      display: 'flex',
      gap: '8px'
    },
    input: {
      flex: 1,
      padding: '10px 12px',
      backgroundColor: '#334155',
      border: '1px solid #475569',
      borderRadius: '8px',
      color: '#f1f5f9',
      fontSize: '13px',
      outline: 'none'
    },
    subscribeBtn: {
      padding: '10px 16px',
      backgroundColor: '#4f46e5',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    divider: {
      height: '1px',
      backgroundColor: '#334155',
      margin: '30px 0 20px'
    },
    bottomBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '16px'
    },
    copyright: {
      color: '#94a3b8',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    legalLinksContainer: {
      display: 'flex',
      gap: '24px',
      flexWrap: 'wrap'
    },
    legalLink: {
      color: '#94a3b8',
      textDecoration: 'none',
      fontSize: '13px',
      transition: 'color 0.2s ease'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      backgroundColor: '#334155',
      borderRadius: '4px',
      color: '#818cf8',
      fontSize: '11px',
      fontWeight: '500'
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing!');
  };

  // Quick links data
  const quickLinks = [
    { to: "/about", label: "About Us" },
    { to: "/features", label: "Features" },
    { to: "/pricing", label: "Pricing" },
    { to: "/case-studies", label: "Case Studies" },
    { to: "/blog", label: "Blog" }
  ];

  // Support links data
  const supportLinks = [
    { to: "/help", label: "Help Center" },
    { to: "/contact", label: "Contact Us" },
    { to: "/documentation", label: "Documentation" },
    { to: "/api", label: "API Status" },
    { to: "/faq", label: "FAQ" }
  ];

  // Legal links data
const legalLinks = [
  { to: "/privacy.html", label: "Privacy Policy" },
  { to: "/terms.html", label: "Terms of Service" },
  { to: "/security.html", label: "Security" },
  { to: "/cookies.html", label: "Cookie Policy" },
  { to: "/delete-account.html", label: "Delete Account" }
];

  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        {/* Main Footer Grid */}
        <div style={styles.grid}>
          {/* Company Info Column */}
          <div style={styles.column}>
            <div style={styles.logoContainer}>
              <img src={logo} alt="ZapDial Logo" style={styles.logo} />
            </div>
            <p style={styles.description}>
              Transform your communication with intelligent dialer solutions. 
              Empowering businesses worldwide with cutting-edge technology.
            </p>
            <div style={styles.socialLinks}>
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <button
                    key={index}
                    style={styles.socialButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = social.color;
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#334155';
                      e.currentTarget.style.color = '#cbd5e1';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onClick={() => handleSocialClick(social.url)}
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <Icon size={18} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Links Column */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Quick Links</h3>
            <div style={styles.linkList}>
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.to}
                  style={styles.link}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#818cf8';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#94a3b8';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <ChevronRight size={12} />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Support Column */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Support</h3>
            <div style={styles.linkList}>
              {supportLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.to}
                  style={styles.link}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#818cf8';
                    e.currentTarget.style.transform = 'translateX(5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#94a3b8';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <ChevronRight size={12} />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact & Newsletter Column */}
          <div style={styles.column}>
            <h3 style={styles.columnTitle}>Contact Us</h3>
            <div style={styles.contactItem}>
              <Phone size={16} color="#818cf8" />
              <span>+91 7024-89-7024</span>
            </div>
            <div style={styles.contactItem}>
              <Mail size={16} color="#818cf8" />
              <span>support@zapdial.com</span>
            </div>
            <div style={styles.contactItem}>
              <MapPin size={16} color="#818cf8" />
              <span>ULHASNAGAR MUMBAI 421003</span>
            </div>

            {/* Newsletter */}
            <div style={styles.newsletterBox}>
              <h4 style={styles.newsletterTitle}>Subscribe to Newsletter</h4>
              <p style={styles.newsletterText}>
                Get the latest updates and offers directly in your inbox.
              </p>
              <form onSubmit={handleSubscribe} style={styles.inputGroup}>
                <input
                  type="email"
                  placeholder="Your email"
                  style={styles.input}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#818cf8'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#475569'}
                  required
                />
                <button
                  type="submit"
                  style={styles.subscribeBtn}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                >
                  <Send size={14} />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Bottom Bar */}
        <div style={styles.bottomBar}>
          <div style={styles.copyright}>
            <img 
              src={logo} 
              alt="ZapDial Logo" 
              style={{ 
                height: '25px', 
                width: 'auto', 
                objectFit: 'contain'
              }} 
            />
            <span>© {currentYear} ZapDial Technologies. All rights reserved.</span>
          </div>
          
          <div style={styles.legalLinksContainer}>
            {legalLinks.map((link, index) => (
              <a
                key={index}
                href={link.to}
                style={styles.legalLink}
                onMouseEnter={(e) => e.currentTarget.style.color = '#818cf8'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div style={styles.badge}>
            <span>🔒</span>
            Secure & Trusted
          </div>
        </div>

        {/* Made with love */}
        <div style={{ textAlign: 'center', marginTop: '20px', color: '#64748b', fontSize: '12px' }}>
          Made with by ZapDial Team
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 1024px) {
          div[style*="grid"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 640px) {
          div[style*="grid"] {
            grid-template-columns: 1fr !important;
          }
          
          div[style*="bottomBar"] {
            flex-direction: column !important;
            text-align: center !important;
          }
          
          div[style*="legalLinksContainer"] {
            justify-content: center !important;
          }
          
          img[style*="width: 250px"] {
            width: 180px !important;
          }
        }
      `}</style>
    </footer>
  );
}